import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Phone, Send, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function AdminSmsCard() {
  const [status, setStatus] = useState(null);
  const [toPhone, setToPhone] = useState("");
  const [body, setBody] = useState("Hope Chest test SMS — your setup is working. Reply STOP to unsubscribe.");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/sms-status").then((r) => setStatus(r.data)).catch(() => { /* noop */ });
  }, []);

  const sendTest = async () => {
    if (!toPhone.trim()) { toast.error("Enter a phone number"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/sms/send-test", { to_phone: toPhone, body });
      toast.success(`SMS queued — Twilio SID ${r.data.sid.slice(0, 12)}…`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "SMS send failed");
    } finally { setBusy(false); }
  };

  const runReminders = async () => {
    if (!window.confirm("Send today's SMS reminder digest to every opted-in Premium user RIGHT NOW? This sends real messages and costs ~$0.008 per recipient.")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/sms/run-reminders-now");
      toast.success(`Reminder job complete — ${r.data.sent} SMS sent.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Job failed");
    } finally { setBusy(false); }
  };

  if (!status) return null;

  return (
    <div className="hc-card p-6 mb-8 print:hidden" data-testid="admin-sms-card">
      <div className="flex items-center gap-3 mb-3">
        <Phone className="w-5 h-5 text-[#3A5A4A]" />
        <h2 className="font-display text-xl">SMS reminders (Twilio)</h2>
      </div>

      <div className="flex items-center gap-4 text-sm mb-4 flex-wrap">
        <div className="flex items-center gap-1.5" data-testid="twilio-verify-status">
          {status.has_verify ? <CheckCircle2 className="w-4 h-4 text-[#3A5A4A]" /> : <XCircle className="w-4 h-4 text-[#C87B67]" />}
          <span className="text-[#7A736E]">Verify service</span>
        </div>
        <div className="flex items-center gap-1.5" data-testid="twilio-sender-status">
          {status.has_sender ? <CheckCircle2 className="w-4 h-4 text-[#3A5A4A]" /> : <XCircle className="w-4 h-4 text-[#C87B67]" />}
          <span className="text-[#7A736E]">Outbound SMS</span>
        </div>
        {status.from_number && (
          <span className="text-xs text-[#7A736E]">From: <strong className="text-[#3D3935]">{status.from_number}</strong></span>
        )}
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#7A736E]">Send a test SMS to any number</label>
          <div className="flex gap-2 mt-1.5 flex-wrap">
            <Input
              type="tel"
              placeholder="+15551234567 or (555) 123-4567"
              value={toPhone}
              onChange={(e) => setToPhone(e.target.value)}
              className="rounded-xl border-[#EBE5DF] flex-1 min-w-[200px]"
              data-testid="admin-sms-to"
            />
            <Button onClick={sendTest} disabled={busy || !status.has_sender} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="admin-sms-send">
              <Send className="w-4 h-4 mr-1.5" /> Send test
            </Button>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#7A736E]">Message body</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            className="w-full mt-1.5 px-4 py-3 rounded-xl border border-[#EBE5DF] focus:outline-none focus:border-[#3A5A4A] text-sm"
            data-testid="admin-sms-body"
          />
          <p className="text-xs text-[#7A736E] mt-1">{body.length} / 160 characters per segment. Long messages split into multiple billed segments.</p>
        </div>
        <div className="pt-3 border-t border-[#EBE5DF] flex flex-wrap gap-2 items-center justify-between">
          <p className="text-xs text-[#7A736E]">Reminder digest runs daily at 13:00 UTC (~8am ET).</p>
          <Button onClick={runReminders} disabled={busy || !status.has_sender} variant="outline" className="rounded-full border-[#EBE5DF] text-sm" data-testid="admin-sms-run-reminders">
            Run reminder digest now
          </Button>
        </div>
      </div>
    </div>
  );
}
